// src/lib/voiceTranscribeInline.js
//
// Turns a journal recording into text WITHOUT storing it: the recording is
// cut into pieces (src/lib/voiceAudio.js, see there for why) and each piece
// goes to voice-to-text's sync recognize, the same path the declined-consent
// recorder and the author portal's Add Book mic already use.
//
// VoiceNoteRecorder (src/pages/ProfileBlocks.jsx) calls this in two places:
//   - the upload to storage failed, so there is no stored copy to
//     transcribe, and without this the reader's words were simply lost;
//   - the upload worked but long-running transcription of the stored copy
//     failed or timed out (voiceTranscription.js), so the recording is kept
//     but had no text.
// Either way the recording is still in memory, so it can still be heard.
//
// Nothing here is stored anywhere. Sync recognize sends the audio to Google
// and keeps nothing, which is why the declined-consent path ("don't save,
// just record") already uses it.

import * as Sentry from '@sentry/react';
import { supabase } from './supabase';
import {
  bytesToBase64, encodeWav16, parseWav, resample, splitIntoPieces, SYNC_RECOGNIZE_RATE_HZ,
} from './voiceAudio';

// Per-piece deadline. Must stay above the function's own sequential inner
// budgets, OAuth exchange then recognize, or the browser hangs up on a
// request that would have answered. The two mirrored values are, in
// supabase/functions/voice-to-text/index.ts, GOOGLE_OAUTH_TIMEOUT_MS (8s)
// and GOOGLE_RECOGNIZE_TIMEOUT_MAX_MS (20s); this file sends
// recognize_timeout_ms at that max. Same shape and same numbers as
// useVoiceRecorder.js's invokeTimeoutMs, which this cannot import (that
// file is mirrored with reanlo-author and keeps its constants private).
const GOOGLE_OAUTH_TIMEOUT_MS_MIRROR = 8000;
const RECOGNIZE_TIMEOUT_MS = 20000;
const INVOKE_ROUND_TRIP_MARGIN_MS = 3000;
const PIECE_INVOKE_TIMEOUT_MS = GOOGLE_OAUTH_TIMEOUT_MS_MIRROR + RECOGNIZE_TIMEOUT_MS + INVOKE_ROUND_TRIP_MARGIN_MS;

// Same unwrapping as voiceNoteStorage.js's callVoiceNoteStorage: a non-2xx
// reply arrives as invokeError, and the function's own { error } message is
// only reachable through invokeError.context.
async function recognizePiece(audioBase64) {
  const { data, error } = await supabase.functions.invoke('voice-to-text', {
    body: { audio_base64: audioBase64, mime_type: 'audio/wav', recognize_timeout_ms: RECOGNIZE_TIMEOUT_MS },
    timeout: PIECE_INVOKE_TIMEOUT_MS,
  });
  if (error) {
    let message = error.message;
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch {
      // not JSON, or already read: keep error.message
    }
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.error);
  return data?.status === 'empty' ? '' : (data?.transcript || '').trim();
}

// Resolves to exactly one of, never throws:
//   { status: 'ok', transcript }       every piece answered, some had words
//   { status: 'empty', transcript: '' } every piece answered, none had words
//   { status: 'partial', transcript }  some pieces failed, the rest had words
//   { status: 'failed', transcript: '' } nothing usable came back
// pieces and failedPieces ride along so the caller and Sentry can say how
// much was lost. `where` names the caller's situation in the Sentry report.
export async function transcribeRecordingInline(blob, { where = 'unknown' } = {}) {
  let pieces = [];
  let samples16k;
  try {
    const buffer = typeof blob.arrayBuffer === 'function'
      ? await blob.arrayBuffer()
      : await new Response(blob).arrayBuffer();
    const { samples, sampleRate } = parseWav(buffer);
    samples16k = resample(samples, sampleRate, SYNC_RECOGNIZE_RATE_HZ);
    pieces = splitIntoPieces(samples16k, SYNC_RECOGNIZE_RATE_HZ);
  } catch (err) {
    console.error('[voice inline transcribe] could not read the recording:', err.message);
    Sentry.captureException(new Error(`voice inline transcribe (${where}): could not read the recording: ${err.message}`), {
      tags: { voice_step: 'inline_transcribe', voice_where: where },
    });
    return { status: 'failed', transcript: '', pieces: 0, failedPieces: 0 };
  }

  const texts = [];
  const failures = [];
  for (let i = 0; i < pieces.length; i++) {
    const [start, end] = pieces[i];
    try {
      const wav = encodeWav16(samples16k.subarray(start, end), SYNC_RECOGNIZE_RATE_HZ);
      const text = await recognizePiece(bytesToBase64(wav));
      if (text) texts.push(text);
    } catch (err) {
      console.error(`[voice inline transcribe] piece ${i + 1} of ${pieces.length} failed:`, err.message);
      failures.push(`piece ${i + 1}: ${err.message}`);
    }
  }

  if (failures.length) {
    Sentry.captureException(new Error(`voice inline transcribe (${where}): ${failures.length} of ${pieces.length} piece(s) failed — ${failures.join('; ')}`), {
      tags: { voice_step: 'inline_transcribe', voice_where: where },
    });
  }

  const transcript = texts.join(' ');
  const result = { transcript, pieces: pieces.length, failedPieces: failures.length };
  if (!failures.length) return { ...result, status: transcript ? 'ok' : 'empty' };
  return transcript ? { ...result, status: 'partial' } : { ...result, status: 'failed', transcript: '' };
}
